import { ChevronRightIcon, DocumentTextIcon } from '@heroicons/react/24/outline';
import { FC } from 'react';

import { useTranslations } from '../hooks/useTranslations.ts';
import { PegaApplication } from '../journey/pega/apiGetPegaState.ts';
import { translationKeys } from '../translations/translationKeys.ts';

type Props = {
  applications: PegaApplication[];
  locale?: string;
  canStartNew?: boolean;
  onSelectApplication: (application: PegaApplication) => void;
  onStartNew: () => void;
};

function formatSavedDate(iso: string, locale?: string, translate?: (key: translationKeys) => string): string | null {
  if (!iso?.trim()) {
    return null;
  }

  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) {
    return null;
  }

  const dateOptions: Intl.DateTimeFormatOptions = {
    day: 'numeric',
    month: 'long',
  };
  const timeOptions: Intl.DateTimeFormatOptions = {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  };

  const day = locale ? date.toLocaleDateString(locale, dateOptions) : date.toLocaleDateString(undefined, dateOptions);
  const time = (locale ? date.toLocaleTimeString(locale, timeOptions) : date.toLocaleTimeString(undefined, timeOptions))
    .toLowerCase()
    .replace(' ', '');
  return `${translate?.(translationKeys.SAVED_ON)} ${day} ${time}`;
}

function getApplicationLabel(application: PegaApplication, translate: (key: translationKeys) => string): string {
  return (
    application.productDescription?.trim() || application.productName?.trim() || translate(translationKeys.APPLICATION)
  );
}

const InProgressApplicationsModal: FC<Props> = ({
  applications,
  locale,
  canStartNew = true,
  onSelectApplication,
  onStartNew,
}) => {
  const { translate } = useTranslations();
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="bg-white rounded-2xl shadow-lg w-full max-w-sm mx-4 px-6 pt-8 pb-8 flex flex-col max-h-[90vh]">
        <h2 className="text-2xl font-bold text-content-primary mb-3">
          {translate(translationKeys.APPLICATION_IN_PROGRESS)}
        </h2>

        <p className="text-sm text-content-secondary mb-6">
          {canStartNew
            ? translate(translationKeys.SELECT_APPLICATION_OR_START_NEW)
            : translate(translationKeys.SELECT_AN_APPLICATION)}
        </p>

        <div className="flex flex-col gap-2 mb-6 overflow-y-auto">
          {applications.map((application) => {
            const savedDate = formatSavedDate(application.updateDateTime, locale, translate);

            return (
              <button
                key={application.applicationAssignmentKey}
                type="button"
                onClick={() => onSelectApplication(application)}
                className="w-full flex items-center gap-3 p-4 rounded-lg border border-azure-20 bg-azure-10 text-left cursor-pointer hover:bg-azure-20/60 transition-colors"
              >
                <DocumentTextIcon className="w-5 h-5 flex-shrink-0 text-[#3B5BDB]" />
                <div className="flex flex-col flex-1 min-w-0 gap-0.5">
                  <span className="text-sm font-semibold text-[#3B5BDB] leading-5">
                    {getApplicationLabel(application, translate)}
                  </span>
                  {savedDate && <span className="text-xs text-azure-70 leading-4">{savedDate}</span>}
                </div>
                <ChevronRightIcon className="w-4 h-4 flex-shrink-0 text-[#3B5BDB]" />
              </button>
            );
          })}
        </div>

        <button
          type="button"
          onClick={onStartNew}
          disabled={!canStartNew}
          className="w-full py-4 px-4 bg-[#3B5BDB] hover:bg-[#2F4BC4] active:bg-[#2439A8] cursor-pointer transition-colors !text-white rounded-full font-medium text-base disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-[#3B5BDB] disabled:active:bg-[#3B5BDB]"
        >
          {translate(translationKeys.START_NEW_APPLICATION)}
        </button>
      </div>
    </div>
  );
};

export default InProgressApplicationsModal;
